import { useQueryClient } from "@tanstack/react-query";
import { useRef, useState } from "react";
import { useRegisterSW } from "virtual:pwa-register/react";

import { compterOperationsHorsLigneNonSynchronisees } from "../../offline/purge-stockage.js";

/** Activation explicite ; les autres fenêtres conservent leur écran et leur saisie. */
export function MiseAJourPwa() {
  const queryClient = useQueryClient();
  const [enCours, setEnCours] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);
  const activationDemandee = useRef(false);
  const workerActive = useRef(false);

  async function verifierSaisies() {
    if (!navigator.onLine) return "Reconnectez-vous avant d’installer la mise à jour.";
    if (queryClient.isMutating())
      return "Un enregistrement est en cours. Attendez sa confirmation.";
    // Les formulaires historiques n'ont pas tous une garde de sortie. On attend
    // leur fermeture, même vierges, plutôt que de supposer leur saisie sauvegardée.
    if (document.querySelector('form, [role="dialog"], [contenteditable="true"]'))
      return "Terminez votre saisie puis revenez à une liste pour installer la mise à jour.";
    const sortie = new Event("beforeunload", { cancelable: true });
    if (!window.dispatchEvent(sortie))
      return "Une saisie reste ouverte. Enregistrez-la ou fermez-la avant la mise à jour.";
    if (await compterOperationsHorsLigneNonSynchronisees())
      return "Des opérations locales attendent une confirmation. Traitez-les dans le suivi hors ligne avant la mise à jour.";
    return null;
  }

  async function rechargerSiAutorise() {
    workerActive.current = true;
    // Workbox signale aussi l'activation initiée par une autre fenêtre : elle
    // ne vaut jamais consentement au rechargement de cette fenêtre.
    if (!activationDemandee.current) {
      setMiseAJourDisponible(true);
      return;
    }
    try {
      const motif = await verifierSaisies();
      if (motif) setErreur(motif);
      else window.location.reload();
    } catch {
      setErreur(
        "Impossible de vérifier les opérations locales. Réessayez sans fermer votre saisie.",
      );
    } finally {
      activationDemandee.current = false;
      setEnCours(false);
    }
  }

  const {
    needRefresh: [miseAJourDisponible, setMiseAJourDisponible],
    updateServiceWorker,
  } = useRegisterSW({
    immediate: true,
    onNeedReload: () => void rechargerSiAutorise(),
  });

  async function installer() {
    if (activationDemandee.current) return;
    activationDemandee.current = true;
    setEnCours(true);
    setErreur(null);
    try {
      const motif = await verifierSaisies();
      if (motif) {
        setErreur(motif);
        activationDemandee.current = false;
        setEnCours(false);
        return;
      }
      if (workerActive.current) await rechargerSiAutorise();
      else {
        await updateServiceWorker();
        // L'activation est asynchrone ; une réponse absente doit laisser le
        // bouton réessayable, sans rechargement tardif non sollicité.
        window.setTimeout(() => {
          if (!activationDemandee.current) return;
          activationDemandee.current = false;
          setEnCours(false);
          setErreur("La mise à jour n’a pas encore répondu. Vous pouvez réessayer.");
        }, 15_000);
      }
    } catch {
      activationDemandee.current = false;
      setEnCours(false);
      setErreur("La mise à jour n’a pas pu être installée. Vos données locales sont conservées.");
    }
  }

  if (!miseAJourDisponible) return null;

  return (
    <aside
      role="status"
      aria-live="polite"
      className="fixed inset-x-3 bottom-3 z-[70] mx-auto flex max-w-2xl flex-col gap-3 rounded-2xl border border-champagne-300 bg-white p-4 text-champagne-900 shadow-xl sm:flex-row sm:items-center sm:justify-between"
    >
      <div>
        <p className="text-[14px] font-semibold">Une nouvelle version de Crayéa est prête.</p>
        <p className="mt-1 text-[13px] leading-relaxed text-champagne-700">
          Installez-la après avoir terminé vos saisies et confirmé vos opérations hors ligne.
        </p>
        {erreur && <p className="mt-2 text-[13px] text-red-700">{erreur}</p>}
      </div>
      <button
        type="button"
        disabled={enCours}
        onClick={() => void installer()}
        className="ui-btn-primary inline-flex min-h-12 shrink-0 items-center justify-center rounded-xl px-4 text-[14px] font-semibold"
      >
        {enCours ? "Installation…" : "Installer maintenant"}
      </button>
      <button
        type="button"
        disabled={enCours}
        onClick={() => setMiseAJourDisponible(false)}
        className="ui-btn-outline inline-flex min-h-12 shrink-0 items-center justify-center rounded-xl px-4 text-[14px] font-semibold"
      >
        Masquer
      </button>
    </aside>
  );
}
